import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  adminAssignShiftSignup,
  adminRemoveShiftSignup,
  extractApiError,
  fetchAdminVolunteers,
  fetchVolunteerMe,
  fetchVolunteerShifts,
  signupVolunteerShift,
  unsignupVolunteerShift,
} from '../api.js';
import {
  clearVolunteerSession,
  getAdminAuthHeaders,
  getVolunteerMeta,
  getVolunteerSession,
  updateVolunteerMetaFromMe,
} from '../useActiveRoles.js';
import AdminVolunteersPanel from '../components/AdminVolunteersPanel.jsx';

function formatSlot(isoStart, isoEnd) {
  const start = new Date(isoStart);
  const end = new Date(isoEnd);
  const day = start.toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const t1 = start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const t2 = end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${day} · ${t1}–${t2}`;
}

function adminHeaders() {
  const h = getAdminAuthHeaders();
  if (Object.keys(h).length) return h;
  const session = getVolunteerSession();
  return session ? { 'X-Volunteer-Session': session } : {};
}

export default function PlanningBenevoles() {
  const session = getVolunteerSession();
  const meta = getVolunteerMeta();
  const [me, setMe] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [assignPick, setAssignPick] = useState({});

  const isSuperAdmin = Boolean(me?.is_super_admin || meta?.is_super_admin);
  const canAssign = Boolean(
    isSuperAdmin ||
      me?.staff_role === 'admin' ||
      meta?.staff_role === 'admin',
  );

  const refresh = useCallback(async () => {
    const tok = getVolunteerSession();
    if (!tok) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [meData, shiftData] = await Promise.all([
        fetchVolunteerMe(tok),
        fetchVolunteerShifts(tok),
      ]);
      setMe(meData);
      setShifts(shiftData);
      updateVolunteerMetaFromMe(meData);
      const mayAssign =
        meData?.is_super_admin || meData?.staff_role === 'admin';
      if (mayAssign) {
        try {
          const vols = await fetchAdminVolunteers(adminHeaders());
          setVolunteers(vols.filter((v) => v.status === 'active'));
        } catch {
          setVolunteers([]);
        }
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        clearVolunteerSession();
        setError('Session expirée. Demandez un nouveau lien.');
      } else {
        setError(extractApiError(err, 'Impossible de charger le planning.'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const myId = me?.volunteer?.id;

  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of shifts) {
      const key = row.title;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    }
    return [...map.entries()];
  }, [shifts]);

  async function toggleSignup(row, checked) {
    const tok = getVolunteerSession();
    if (!tok) return;
    setBusyId(row.id);
    setNotice(null);
    try {
      if (checked) {
        const res = await signupVolunteerShift(tok, row.id);
        if (res.overlap_warning) {
          setNotice('Attention : ce créneau chevauche une autre de vos inscriptions.');
        }
      } else {
        await unsignupVolunteerShift(tok, row.id);
      }
      await refresh();
    } catch (err) {
      setNotice(extractApiError(err, 'Action impossible.'));
    } finally {
      setBusyId(null);
    }
  }

  async function assignVolunteer(shiftId) {
    const volunteerId = assignPick[shiftId];
    if (!volunteerId) return;
    setBusyId(shiftId);
    setNotice(null);
    try {
      await adminAssignShiftSignup(adminHeaders(), shiftId, volunteerId);
      setAssignPick((p) => ({ ...p, [shiftId]: '' }));
      await refresh();
    } catch (err) {
      setNotice(extractApiError(err, 'Affectation impossible.'));
    } finally {
      setBusyId(null);
    }
  }

  async function removeSignup(shiftId, volunteerId) {
    setBusyId(shiftId);
    setNotice(null);
    try {
      await adminRemoveShiftSignup(adminHeaders(), shiftId, volunteerId);
      await refresh();
    } catch (err) {
      setNotice(extractApiError(err, 'Retrait impossible.'));
    } finally {
      setBusyId(null);
    }
  }

  if (!session) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Planning bénévoles
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Recliquez sur le lien reçu par email (il reste valable tant que votre accès
            est actif), ou demandez un nouveau lien.
          </Typography>
          <Button component={RouterLink} to="/benevoles" variant="contained">
            Page Bénévoles
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Stack spacing={2} sx={{ mb: 4 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={1}
        >
          <Box>
            <Typography variant="h4">Planning bénévoles</Typography>
            <Typography variant="body2" color="text.secondary">
              {me?.volunteer
                ? `${me.volunteer.first_name} ${me.volunteer.last_name}`
                : meta?.first_name
                  ? `${meta.first_name} ${meta.last_name}`
                  : 'Espace bénévole'}
              {me?.volunteer?.access_expires_at && (
                <>
                  {' · accès jusqu’au '}
                  {new Date(me.volunteer.access_expires_at).toLocaleDateString('fr-FR')}
                </>
              )}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={refresh} disabled={loading}>
              Actualiser
            </Button>
            <Button
              startIcon={<LogoutIcon />}
              color="inherit"
              onClick={() => {
                clearVolunteerSession();
                window.location.href = '/benevoles';
              }}
            >
              Déconnexion
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}
        {notice && (
          <Alert severity="warning" onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        {canAssign && (
          <Alert severity="info">
            En tant qu&apos;admin, vous pouvez affecter ou retirer n&apos;importe quel bénévole
            actif (y compris sans email) sur chaque créneau ci-dessous.
          </Alert>
        )}

        {loading && !shifts.length ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        ) : (
          grouped.map(([title, rows]) => (
            <Paper
              key={title}
              elevation={0}
              sx={{ p: 2, border: 1, borderColor: 'divider' }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>
                {title}
              </Typography>
              <Stack spacing={1.5} divider={<Divider flexItem />}>
                {rows.map((row) => {
                  const signed = Boolean(
                    myId && row.signups?.some((s) => s.volunteer_id === myId),
                  );
                  const full = (row.signup_count ?? 0) >= row.capacity;
                  const label = [
                    formatSlot(row.starts_at, row.ends_at),
                    row.role_label,
                    `${row.signup_count ?? 0}/${row.capacity}`,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  const takenIds = new Set(
                    (row.signups || []).map((s) => s.volunteer_id),
                  );
                  const assignable = volunteers.filter((v) => !takenIds.has(v.id));
                  return (
                    <Box key={row.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={signed}
                            disabled={busyId === row.id || (!signed && full)}
                            onChange={(e) => toggleSignup(row, e.target.checked)}
                          />
                        }
                        label={
                          <Stack spacing={0.5}>
                            <Typography variant="body1">{label}</Typography>
                            {row.has_overlap_warning && signed && (
                              <Chip
                                size="small"
                                color="warning"
                                label="Chevauchement avec un autre créneau"
                                sx={{ alignSelf: 'flex-start' }}
                              />
                            )}
                          </Stack>
                        }
                      />
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        useFlexGap
                        spacing={0.5}
                        sx={{ mt: 0.5, ml: 4 }}
                      >
                        {(row.signups || []).length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            Personne inscrit(e) pour l’instant
                          </Typography>
                        ) : (
                          (row.signups || []).map((s) => (
                            <Chip
                              key={s.volunteer_id}
                              size="small"
                              label={`${s.first_name} ${s.last_name}`}
                              onDelete={
                                canAssign
                                  ? () => removeSignup(row.id, s.volunteer_id)
                                  : undefined
                              }
                              deleteIcon={
                                canAssign ? (
                                  <CloseIcon fontSize="small" />
                                ) : undefined
                              }
                              disabled={busyId === row.id}
                            />
                          ))
                        )}
                      </Stack>
                      {canAssign && !full && (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ sm: 'center' }}
                          sx={{ mt: 1, ml: 4 }}
                        >
                          <TextField
                            select
                            size="small"
                            label="Affecter un bénévole"
                            value={assignPick[row.id] || ''}
                            onChange={(e) =>
                              setAssignPick((p) => ({
                                ...p,
                                [row.id]: e.target.value,
                              }))
                            }
                            sx={{ minWidth: 220 }}
                            disabled={busyId === row.id || !assignable.length}
                          >
                            <MenuItem value="">
                              {assignable.length
                                ? 'Choisir…'
                                : 'Tous déjà inscrits'}
                            </MenuItem>
                            {assignable.map((v) => (
                              <MenuItem key={v.id} value={v.id}>
                                {v.last_name} {v.first_name}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={
                              busyId === row.id || !assignPick[row.id]
                            }
                            onClick={() => assignVolunteer(row.id)}
                          >
                            Affecter
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          ))
        )}

        {isSuperAdmin && (
          <>
            <Divider sx={{ my: 1 }} />
            <AdminVolunteersPanel />
          </>
        )}
      </Stack>
    </Container>
  );
}
